import { useEffect, useMemo, useState } from "react";
import { getAllFriends, removeFriendById } from "../../services/friendService";
import "./FriendList.css";

const FriendsList = ({
  token,
  socket,
  user,
  onFriendSelect,
  unreadCounts,
  setUnreadCounts,
}) => {
  const [friends, setFriends] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [error, setError] = useState("");

  // ================= FETCH FRIENDS =================
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const res = await getAllFriends();
        setFriends(res.data);

        // build unread counts
        const unreadObj = {};
        res.data.forEach((friend) => {
          if (friend.chatId) {
            unreadObj[friend.chatId] = friend.unread || 0;
          }
        });
        setUnreadCounts(unreadObj);
      } catch (err) {
        console.error(err.response?.data || err.message);
        setError("Failed to fetch friends");
      }
    };

    fetchFriends();
  }, [token, setUnreadCounts]);

  // ================= SOCKET PRESENCE =================
  useEffect(() => {
    if (!socket) return;

    const handleUserOnline = ({ userId }) => {
      setOnlineUsers((prev) => new Set(prev).add(userId));
    };

    const handleUserOffline = ({ userId }) => {
      setOnlineUsers((prev) => {
        const updated = new Set(prev);
        updated.delete(userId);
        return updated;
      });
    };

    const handleInitialOnlineUsers = (users) => {
      setOnlineUsers(new Set(users));
    };

    socket.on("onlineUsers", handleInitialOnlineUsers);
    socket.on("userOnline", handleUserOnline);
    socket.on("userOffline", handleUserOffline);

    return () => {
      socket.off("onlineUsers", handleInitialOnlineUsers);
      socket.off("userOnline", handleUserOnline);
      socket.off("userOffline", handleUserOffline);
    };
  }, [socket]);

  // ================= SORT FRIENDS =================
  const sortedFriends = useMemo(() => {
    return [...friends].sort((a, b) => {
      const aOnline = onlineUsers.has(a._id);
      const bOnline = onlineUsers.has(b._id);

      // online users first
      if (aOnline !== bOnline) {
        return bOnline - aOnline;
      }

      // both offline → sort by lastSeen (recent first)
      const aLast = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const bLast = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;

      return bLast - aLast;
    });
  }, [friends, onlineUsers]);

  // ================= REMOVE FRIEND =================
  const removeFriend = async (id) => {
    try {
      await removeFriendById(id);
      setFriends((prev) => prev.filter((f) => f._id !== id));
    } catch (err) {
      console.error(err.response?.data || err.message);
    }
  };

  // ================= OPEN CHAT =================
  const handleFriendClick = (friend) => {
    onFriendSelect(friend);

    // reset unread locally
    setUnreadCounts((prev) => ({
      ...prev,
      [friend._id]: 0,
    }));

    socket?.emit("markSeen", {
      friendId: friend._id,
    });
  };

  // ================= FORMAT LAST SEEN =================
  const formatLastSeen = (date) => {
    if (!date) return "Offline";
    const diff = Math.floor((Date.now() - new Date(date)) / 60000);

    if (diff < 1) return "Just now";
    if (diff < 60) return `last seen ${diff} min ago`;
    if (diff < 1440) return `last seen ${Math.floor(diff / 60)} hr ago`;
    return `last seen ${Math.floor(diff / 1440)} day(s) ago`;
  };

  // ================= RENDER =================
  return (
    <div className="friends-list">
      <h3>Friends</h3>

      {error && <p>{error}</p>}
      {!error && sortedFriends.length === 0 && (
        <p>No friends yet? Search and add your classmates to start chatting!</p>
      )}

      <ul>
        {sortedFriends.map((friend) => {
          const isOnline = onlineUsers.has(friend._id);

          return (
            <li key={friend._id} className="friend-item">
              <div
                className="friend-status-wrapper"
                onClick={() => handleFriendClick(friend)}
              >
                {/* Only show dot if online */}
                {isOnline && <span className="status-dot active"></span>}

                <div className="friend-info">
                  <span className="friend-name">{friend.username}</span>

                  {!isOnline && (
                    <small className="last-seen">
                      {friend.lastSeen
                        ? `${formatLastSeen(friend.lastSeen)}`
                        : "Offline"}
                    </small>
                  )}
                </div>

                {/* Unread badge */}
                {unreadCounts?.[friend._id] > 0 && (
                  <span className="unread-badge">
                    {unreadCounts[friend._id]}
                  </span>
                )}
              </div>

              <button
                className="inbox-btn"
                onClick={() => handleFriendClick(friend)}
              >
                Inbox
              </button>

              <button
                className="remove-btn"
                onClick={() => removeFriend(friend._id)}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FriendsList;
